import React, { useEffect, useState } from 'react';
import { Screen } from '../App';
import { User } from '../storage';
import * as authApi from '../authApi';

interface Props { navigate: (s: Screen) => void; user: User | null; }

// Opens Stripe's billing portal (update card, invoices, cancel). If the portal
// can't be opened — no Stripe customer, Stripe unreachable — it never dead-ends:
// it offers a one-tap "request cancellation" that reaches a human. A teacher
// always has a way out.
export default function Billing({ navigate, user }: Props) {
  const [state, setState] = useState<'opening' | 'escape' | 'requested'>('opening');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = await authApi.createPortalSession();
        if (!cancelled) window.location.href = url; // hand off to Stripe's hosted portal
      } catch (e: any) {
        console.error('billing portal open failed', e);
        if (!cancelled) setState('escape');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const requestCancel = async () => {
    setSubmitting(true); setError('');
    try {
      await authApi.requestCancellation();
      setState('requested');
    } catch (e: any) {
      setError(e?.message || 'Could not submit your request. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="top-bar">
        <button onClick={() => navigate('dashboard')} style={{ background: 'none', color: 'var(--teal)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><polyline points="15,18 9,12 15,6" /></svg>
          Dashboard
        </button>
        <span className="top-bar-logo" style={{ fontSize: 15 }}>Subscription</span>
        <div style={{ width: 60 }} />
      </div>

      <div className="page-content" style={{ paddingTop: 40 }}>
        {state === 'opening' && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div className="spinner" />
            <p style={{ color: 'var(--teal)', marginTop: 16, fontWeight: 600 }}>Opening your billing portal…</p>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 6 }}>You'll be taken to our secure payment provider.</p>
          </div>
        )}

        {state === 'escape' && (
          <div>
            <div className="card" style={{ marginBottom: 16 }}>
              <h2 style={{ marginBottom: 10 }}>Manage or cancel your subscription</h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.7, marginBottom: 8 }}>
                We couldn't open the payment portal automatically for this account right now. You can still cancel — tap the button below and we'll process your cancellation and confirm by email, usually within one business day.
              </p>
              <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.6 }}>
                You won't be charged again once your cancellation is processed, and you keep access through any period you've already paid for.
              </p>
            </div>
            {error && <div style={{ background: 'var(--coral-light)', color: 'var(--coral)', padding: '12px 16px', borderRadius: 8, fontSize: 14, marginBottom: 16 }}>{error}</div>}
            <button onClick={requestCancel} className="btn-primary" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Request cancellation'}
            </button>
            <button onClick={() => setState('opening')} style={{ background: 'none', color: 'var(--teal)', width: '100%', padding: '12px', marginTop: 8, fontSize: 13, fontWeight: 600, border: 'none', cursor: 'pointer' }}>
              Try the payment portal again
            </button>
            <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-secondary)', marginTop: 16 }}>
              Prefer email? Write to <span style={{ color: 'var(--teal)', fontWeight: 600 }}>info@werhope.org</span>.
            </p>
          </div>
        )}

        {state === 'requested' && (
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <p style={{ fontSize: 40, marginBottom: 16 }}>✅</p>
            <h2 style={{ marginBottom: 12 }}>Cancellation requested</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 15, lineHeight: 1.7 }}>
              We've received your request and will process it shortly — you'll get an email confirmation. You keep access through any time you've already paid for.
            </p>
            <button onClick={() => navigate('dashboard')} className="btn-primary" style={{ marginTop: 24 }}>Back to Dashboard</button>
          </div>
        )}
      </div>
    </div>
  );
}
